import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './hooks/useAuth';

// En la app nativa Android el destino por defecto es el TPV, no el Home del venue.
const IS_NATIVE = Capacitor.isNativePlatform();
import Login from './pages/Login';
import OwnerPanel from './pages/OwnerPanel';
import AdminPanel from './pages/AdminPanel';
import RrppPanel from './pages/RrppPanel';
import SocioPanel from './pages/SocioPanel';
import DoorScan from './pages/DoorScan';
import BuyerLink from './pages/BuyerLink';
import Invitacion from './pages/Invitacion';
import MesaLink from './pages/MesaLink';
import Mozas from './pages/Mozas';
import Tpv from './pages/Tpv';
import MiCuenta from './pages/MiCuenta';
import Home from './pages/Home';
import RegisterInvite from './pages/RegisterInvite';
import { useState, useEffect } from 'react';
import VenueNotFound from './components/VenueNotFound';
import EventPublic from './pages/EventPublic';
import CumpleForm from './pages/CumpleForm';
import TicketView from './pages/TicketView';
import Signup from './pages/Signup';
import Landing from './pages/Landing';
import CentroAyuda from './pages/CentroAyuda';
import Privacidad from './pages/Privacidad';
import GuiaCrearEvento from './pages/GuiaCrearEvento';
import GuiaConectarMP from './pages/GuiaConectarMP';
import GuiaCajasTPV from './pages/GuiaCajasTPV';
import GuiaGuardarropa from './pages/GuiaGuardarropa';
import GuiaCierreCaja from './pages/GuiaCierreCaja';
import GuiaInventario from './pages/GuiaInventario';
import GuiaEntradasVendidas from './pages/GuiaEntradasVendidas';
import GuiaPromociones from './pages/GuiaPromociones';
import GuiaEmailMkt from './pages/GuiaEmailMkt';
import GuiaMesas from './pages/GuiaMesas';
import GuiaPuntos from './pages/GuiaPuntos';
import GuiaMiPlan from './pages/GuiaMiPlan';
import PremiosControl from './pages/PremiosControl';
import GuiaRRPP from './pages/GuiaRRPP';
import GuiaGruposLotes from './pages/GuiaGruposLotes';
import SuperAdmin from './pages/SuperAdmin';
import { isPlatformRoot, getTenantSlug } from './tenant';

const API = process.env.REACT_APP_API_URL || '';

function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/admin" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function RequireSuperadmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/admin" replace />;
  if (!user.isSuperadmin) return <Navigate to="/admin/owner" replace />;
  return children;
}

function Spinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: '0.3em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>
        Cargando...
      </div>
    </div>
  );
}

function AutoRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/admin" replace />;
  const map = { owner: '/admin/owner', admin: '/admin/panel', socio: '/admin/socio', rrpp: '/admin/rrpp' };
  return <Navigate to={map[user.role] || '/admin'} replace />;
}

const IS_STAGING = process.env.REACT_APP_ENV === 'staging';

export default function App() {
  return (
    <AuthProvider>
      {IS_STAGING && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999, background: '#f1c40f', color: '#000', textAlign: 'center', fontSize: 11, fontWeight: 800, letterSpacing: '0.2em', padding: '3px 0', pointerEvents: 'none', fontFamily: 'monospace' }}>
          STAGING · DATOS DE PRUEBA
        </div>
      )}
      <BrowserRouter>
        <Routes>
          {/* Login */}
          <Route path="/admin" element={<LoginGuard />} />

          {/* Paneles según rol */}
          <Route path="/admin/owner" element={
            <RequireAuth roles={['owner']}>
              <OwnerPanel />
            </RequireAuth>
          } />
          <Route path="/admin/panel" element={
            <RequireAuth roles={['admin']}>
              <AdminPanel />
            </RequireAuth>
          } />
          <Route path="/admin/rrpp" element={
            <RequireAuth roles={['rrpp']}>
              <RrppPanel />
            </RequireAuth>
          } />
          <Route path="/admin/socio" element={
            <RequireAuth roles={['socio']}>
              <SocioPanel />
            </RequireAuth>
          } />

          {/* Escáner de puerta (público, sin login) */}
          <Route path="/scan/:token" element={<DoorScan />} />

          {/* Link de compra del RRPP (público, sin login) */}
          <Route path="/l/:code" element={<BuyerLink />} />
          {/* Invitación entre eventos: la entrada nace recién al aceptarla. */}
          <Route path="/i/:code" element={<Invitacion />} />
          <Route path="/mesa/:code" element={<MesaLink />} />
          <Route path="/mozas" element={<Mozas />} />

          {/* TPV — acceso por PIN, sin login de usuario */}
          <Route path="/tpv" element={<Tpv />} />

          {/* Compra online del evento (público) */}
          <Route path="/e/:eventId" element={<EventPublic />} />

          {/* Anotarse en la lista de cumpleaños. Página propia para poder compartir SU
              link en una historia de Instagram. */}
          <Route path="/e/:eventId/cumple" element={<CumpleForm />} />

          {/* Ver / descargar una entrada (público) */}
          <Route path="/t/:code" element={<TicketView />} />

          {/* Mi cuenta del comprador (público) */}
          <Route path="/cuenta/:slug" element={<MiCuenta />} />
          <Route path="/premios/:token" element={<PremiosControl />} />

          {/* Home público del venue por slug */}
          <Route path="/sitio/:slug" element={<Home />} />

          {/* Registro por invitación (RRPP / staff) */}
          <Route path="/registro/:token" element={<RegisterInvite />} />

          {/* Alta self-service de un boliche (formulario) */}
          <Route path="/signup" element={<Signup />} />

          {/* Panel super-admin de la plataforma (solo emails en SUPERADMIN_EMAILS) */}
          <Route path="/superadmin" element={<RequireSuperadmin><SuperAdmin /></RequireSuperadmin>} />

          {/* Landing comercial — accesible directo (en la raíz tukipro.com se sirve sola) */}
          <Route path="/landing" element={<Landing />} />

          {/* Centro de ayuda / guías de uso (público) */}
          <Route path="/ayuda" element={<CentroAyuda />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/ayuda/crear-evento" element={<GuiaCrearEvento />} />
          <Route path="/ayuda/conectar-mercado-pago" element={<GuiaConectarMP />} />
          <Route path="/ayuda/rrpp" element={<GuiaRRPP />} />
          <Route path="/ayuda/grupos-de-lotes" element={<GuiaGruposLotes />} />
          <Route path="/ayuda/cajas-tpv" element={<GuiaCajasTPV />} />
          <Route path="/ayuda/guardarropa" element={<GuiaGuardarropa />} />
          <Route path="/ayuda/cierre-de-caja" element={<GuiaCierreCaja />} />
          <Route path="/ayuda/inventario" element={<GuiaInventario />} />
          <Route path="/ayuda/entradas-vendidas" element={<GuiaEntradasVendidas />} />
          <Route path="/ayuda/promociones" element={<GuiaPromociones />} />
          <Route path="/ayuda/email-marketing" element={<GuiaEmailMkt />} />
          <Route path="/ayuda/mesas" element={<GuiaMesas />} />
          <Route path="/ayuda/sistema-de-puntos" element={<GuiaPuntos />} />
          <Route path="/ayuda/mi-plan" element={<GuiaMiPlan />} />

          {/* Raíz: en la app nativa va al TPV; en web, home del venue (por dominio) */}
          <Route path="/" element={IS_NATIVE ? <Navigate to="/tpv" replace /> : <RootRoute />} />
          <Route path="*" element={<Navigate to={IS_NATIVE ? '/tpv' : '/admin'} replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function RootRoute() {
  const [notFound, setNotFound] = useState(false);
  // En la raíz de la plataforma (tukipro.com) va la landing comercial; sus CTA
  // llevan a /signup (el alta self-service). En el subdominio de un venue (o hosts
  // actuales de Railway) va su home pública.
  if (isPlatformRoot()) return <Landing />;
  if (notFound) return <AutoRedirect />;
  return <Home onNotFound={() => setNotFound(true)} />;
}

function LoginGuard() {
  const { user, loading } = useAuth();
  // Coherencia con la home: si el subdominio presenta un slug que NO existe, mostramos
  // "Boliche no encontrado" en vez del login. La raíz (sin subdominio → getTenantSlug null,
  // cae al fallback) y el APK nativo NO validan → login normal. Ante error de red no bloqueo.
  const slug = IS_NATIVE ? null : getTenantSlug();
  const [venue, setVenue] = useState(slug ? 'checking' : 'ok');
  useEffect(() => {
    if (!slug) { setVenue('ok'); return; }
    let alive = true;
    fetch(`${API}/api/public/home/${slug}`)
      .then(r => { if (alive) setVenue(r.status === 404 ? 'notfound' : 'ok'); })
      .catch(() => { if (alive) setVenue('ok'); });
    return () => { alive = false; };
  }, [slug]);

  if (loading || venue === 'checking') return <Spinner />;
  if (venue === 'notfound') return <VenueNotFound />;
  if (user) {
    const map = { owner: '/admin/owner', admin: '/admin/panel', socio: '/admin/socio', rrpp: '/admin/rrpp' };
    return <Navigate to={map[user.role] || '/admin/owner'} replace />;
  }
  return <Login />;
}
